/* eslint-disable @typescript-eslint/no-explicit-any */
import axios, { AxiosError, AxiosInstance, AxiosResponse } from 'axios';
import { DateTime } from 'luxon';

import history from './history';
import LocalStorageWrapper from './localStorageWrapper';

type AuthTokensType = {
  access: string;
  refresh: string;
  expiresIn: number;
};

type PromiseResolveRejectType = {
  resolve: (value?: unknown) => void;
  reject: (reason?: any) => void;
};

/**
 * In remote mode, set REACT_APP_USE_PROXY=true so requests go to the same
 * origin (CRA dev server) and are forwarded via setupProxy.js — avoids CORS.
 */
const USE_PROXY = process.env.REACT_APP_USE_PROXY === 'true';
const HOST = USE_PROXY ? '' : process.env.REACT_APP_URL || 'http://localhost:8000';

const ACCESS_TOKEN_EXPIRES_IN = 1800000; // 30 minutes

// for multiple requests. Module-level on purpose: the refresh is per session,
// not per client, so two instances refreshing at once would race.
let isRefreshing = false;
let failedQueue: PromiseResolveRejectType[] = [];

const processQueue = (error: AxiosError | null, tokens: AuthTokensType | null): void => {
  failedQueue.forEach((prom) => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(tokens);
    }
  });
  failedQueue = [];
};

/**
 * Installs the auth pair on an axios instance: attach the access token, and on a
 * 401 refresh it once while queueing anything else that failed meanwhile.
 *
 * Exported so clients pointed at other hosts (the support service) get the same
 * session handling instead of a partial copy — half of this pair is worse than
 * none, because the request keeps succeeding for thirty minutes and then starts
 * failing with no path to recovery.
 */
export const attachAuthInterceptors = (instance: AxiosInstance): AxiosInstance => {
  // The interceptor here ensures that we check for the token in local storage
  // every time an ajax request is made
  instance.interceptors.request.use(
    (config) => {
      const tokens = LocalStorageWrapper.getItem('authTokens');
      if (tokens && config?.headers) {
        // eslint-disable-next-line no-param-reassign, @typescript-eslint/no-unsafe-member-access
        config.headers.Authorization = `Bearer ${tokens.access}`;
      }
      return config;
    },
    (error) => {
      return Promise.reject(error);
    }
  );

  instance.interceptors.response.use(
    (response) => {
      return response;
    },
    (error: AxiosError): Promise<any> => {
      const originalRequest = { ...error.config, retry: false };
      if (error?.response?.status === 401 && !originalRequest.retry) {
        if (isRefreshing) {
          return new Promise<any>((resolve, reject) => {
            failedQueue.push({ resolve, reject });
          })
            .then((tokens: AuthTokensType) => {
              if (originalRequest?.headers) {
                // eslint-disable-next-line no-param-reassign, @typescript-eslint/no-unsafe-member-access
                originalRequest.headers.Authorization = `Bearer ${tokens.access}`;
              }

              return axios(originalRequest);
            })
            .catch((err: unknown) => {
              return err;
            });
        }

        originalRequest.retry = true;
        isRefreshing = true;

        const tokens = LocalStorageWrapper.getItem('authTokens') || {
          refresh: 'NOT VALID',
        };
        return new Promise((resolve, reject) => {
          // The refresh always goes to cosmos (HOST), never to the instance's
          // own baseURL: only cosmos mints tokens.
          // eslint-disable-next-line @typescript-eslint/no-floating-promises
          axios
            .post(`${HOST}/api/token/refresh`, { refresh: tokens.refresh })
            .then((response: AxiosResponse<AuthTokensType>) => {
              const newAuthTokens = response.data;
              newAuthTokens.expiresIn = DateTime.local().valueOf() + ACCESS_TOKEN_EXPIRES_IN;
              LocalStorageWrapper.setItem('authTokens', newAuthTokens);
              processQueue(null, response.data);
              resolve(axios(originalRequest));
            })
            .catch((err: AxiosError) => {
              processQueue(err, null);
              LocalStorageWrapper.removeItem('authTokens');
              history.push('/login');
              reject(err);
            })
            .then(() => {
              isRefreshing = false;
            });
        });
      }

      return Promise.reject(error);
    }
  );

  return instance;
};

const API = attachAuthInterceptors(axios.create({ baseURL: HOST }));

export default API;
